// diamond/primitives.js — Diamond UI's shared building blocks.
//
// Dependency-free (inline styles on tokens.js), so every Diamond surface
// draws from one set of parts instead of re-inventing tiles, rings,
// sparklines and sheets per panel. Interaction conventions live here too:
// count-up numbers, pointer-scrub charts, spring sheets.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { hapticTap } from "../../utils/haptics";
import { soundTick } from "../../utils/sound";
import { Z, TYPE, RADIUS, glass, glow } from "./tokens";

export const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";
export const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";

// ── Formatting ───────────────────────────────────────────────────────────
export const fmtZ = (v, d = 2) => {
  const n = Number(v || 0);
  return `${n < 0 ? "-" : ""}Ⓩ${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};
// toLocaleString builds a fresh Intl.NumberFormat on every call — measured
// as a visible slice of the drawer's open (23 rows × several figures each,
// re-run on every poll). One cached formatter per digit count instead.
const _nf = {};
const _num = (n, d) => (_nf[d] || (_nf[d] = new Intl.NumberFormat("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }))).format(n);
export const fmtZSigned = (v, d = 2) => {
  const n = Number(v || 0);
  return `${n >= 0 ? "+" : "-"}Ⓩ${_num(Math.abs(n), d)}`;
};
export const fmtUsd = (v, d = 2) => {
  const n = Number(v || 0);
  return `${n < 0 ? "-" : ""}$${_num(Math.abs(n), d)}`;
};
export const fmtUsdSigned = (v, d = 2) => {
  const n = Number(v || 0);
  return `${n >= 0 ? "+" : "-"}$${_num(Math.abs(n), d)}`;
};
// One P&L figure, formatted in whichever currency the app-wide Ⓩ/$ toggle
// is set to — the single source every panel should call instead of picking
// fmtZSigned vs fmtUsdSigned inline (that's how the same figure ended up
// labeled two different ways in different corners of the UI before).
export const fmtCurrencySigned = (currency, v, d = 2) => (currency === "usd" ? fmtUsdSigned(v, d) : fmtZSigned(v, d));
export const fmtPctSigned = (v, d = 1) => {
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v || 0);
  return `${n >= 0 ? "+" : ""}${n.toFixed(d)}%`;
};
export const pnlColor = (T, v) => (Number(v) > 0 ? T.green : Number(v) < 0 ? T.red : T.muted);
export const fmtWhen = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
  } catch { return ""; }
};
export const fmtDay = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" }); } catch { return ""; }
};

// ── Count-up number — eases from the previous value to the next on every
// change so a live-polled figure visibly moves instead of snapping. ───────
// Count-up tween. Every animated figure on screen used to commit a React
// state update on EVERY frame for 650 ms after each poll — ~40 renders per
// figure per tick, and the leaderboard alone had 23 of them, so each 5 s
// poll landed as a burst of ~900 renders and a visible hitch on a phone
// (measured: ~100 style recalcs per poll on the floor tab). Now: the tween
// commits at most every other frame (~30 updates/s, imperceptible for a
// number), snaps straight to the target while the tab is hidden, and skips
// the tween entirely for a sub-cent change.
export function useCountUp(value, duration = 650) {
  const target = Number.isFinite(Number(value)) ? Number(value) : 0;
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return undefined;
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    if (hidden || Math.abs(target - from) < 0.005) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }
    const start = performance.now();
    cancelAnimationFrame(rafRef.current);
    let frame = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      frame += 1;
      if (t >= 1 || frame % 2 === 0) {
        const e = 1 - Math.pow(1 - t, 3);
        setShown(from + (target - from) * e);
      }
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return shown;
}

export function AnimatedNumber({ value, format = (v) => v.toFixed(2), style }) {
  const v = useCountUp(value);
  return <span style={style}>{format(v)}</span>;
}

// ── Layout atoms ─────────────────────────────────────────────────────────
export function SectionLabel({ children, T, right, style }) {
  return (
    // flexShrink:0 by default — this is almost always a direct child of a
    // scrollable flex column; see the StatTile/Collapsible comment above on
    // why a shrinkable header can end up squeezed instead of the column
    // just scrolling past it.
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "14px 0 8px", flexShrink: 0, ...style }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.08, textTransform: "uppercase", color: T.muted }}>{children}</span>
      {right && <span style={{ fontSize: 10, color: T.faint }}>{right}</span>}
    </div>
  );
}

export function Chip({ children, T, color, soft = true, size = 9.5, style, testId }) {
  const c = color || T.accent;
  return (
    <span data-testid={testId} style={{
      display: "inline-flex", alignItems: "center", gap: 4, fontSize: size, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase",
      padding: "2px 7px", borderRadius: RADIUS.sm, color: soft ? c : "#fff", background: soft ? `${c}1f` : c, border: `1px solid ${c}55`,
      whiteSpace: "nowrap", ...style,
    }}>{children}</span>
  );
}

export function StatTile({ label, value, sub, color, T, accent, onClick, testId, children, style }) {
  return (
    <div data-testid={testId} onClick={onClick} style={{
      ...glass(T, { padding: "11px 12px" }), position: "relative", overflow: "hidden", minWidth: 0,
      // A flex item's automatic min-size resolves to 0 (not content height)
      // once it has any non-visible overflow — inside a height-bounded flex
      // column (e.g. a Sheet), that lets the browser SHRINK this tile below
      // its content instead of letting the column scroll, clipping it down
      // to a sliver. flexShrink:0 opts out regardless of where this lands.
      flexShrink: 0,
      cursor: onClick ? "pointer" : "default", transition: `transform 180ms ${EASE}, box-shadow 220ms ease`,
      // Always present (never conditionally spread in/out) so a caller that
      // toggles `accent` across renders — e.g. AgentSheet's zBucks/USD
      // currency switch reusing the same grid position for a different
      // tile — never removes `borderColor` while glass()'s `border`
      // shorthand stays set on the same node. React warns on exactly that
      // (mixing shorthand/longhand across a rerender is a real styling-bug
      // risk, not just noise) — keeping both keys always-present, with a
      // plain default when there's no accent, sidesteps it entirely.
      borderColor: accent ? `${accent}44` : T.border,
      boxShadow: accent ? `inset 0 1px 0 ${accent}22, 0 0 22px ${accent}14` : "none",
      ...style,
    }}>
      {accent && <span style={{ position: "absolute", top: -30, right: -30, width: 90, height: 90, borderRadius: RADIUS.pill, background: `radial-gradient(${accent}33, transparent 70%)`, pointerEvents: "none" }} />}
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.08, textTransform: "uppercase", color: T.muted }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 900, lineHeight: 1.15, color: color || T.text, fontFamily: TYPE.mono, marginTop: 3, letterSpacing: -0.3 }}>{value}</div>
      {sub && <div style={{ fontSize: 9.5, color: T.faint, marginTop: 2, lineHeight: 1.3 }}>{sub}</div>}
      {children}
    </div>
  );
}

export function ProgressBar({ pct, color, T, height = 6, track, style, animate = true }) {
  const w = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ height, borderRadius: RADIUS.pill, background: track || T.surface2, overflow: "hidden", ...style }}>
      <div style={{ height: "100%", width: `${w}%`, borderRadius: RADIUS.pill, background: color || T.accent, transition: animate ? `width 700ms ${EASE}` : "none", boxShadow: `0 0 10px ${color || T.accent}66` }} />
    </div>
  );
}

export function SegmentedControl({ options, value, onChange, T, color, testIdPrefix, size = 11 }) {
  const c = color || T.accent;
  return (
    <div style={{ display: "flex", gap: 2, padding: 3, ...glass(T, { borderRadius: RADIUS.md }) }}>
      {options.map(([id, label]) => {
        const on = id === value;
        return (
          <button key={id} type="button" data-testid={testIdPrefix ? `${testIdPrefix}-${id}` : undefined}
            onClick={() => { hapticTap(); soundTick(); onChange(id); }} style={{
              flex: 1, padding: "7px 4px", borderRadius: RADIUS.sm, border: "none", cursor: "pointer",
              background: on ? `${c}22` : "transparent", color: on ? c : T.muted, fontSize: size, fontWeight: 800, fontFamily: TYPE.sans,
              transition: "background 200ms ease, color 200ms ease, box-shadow 220ms ease", whiteSpace: "nowrap",
              ...(on ? glow(T, 0.4, c) : {}),
            }}>{label}</button>
        );
      })}
    </div>
  );
}

// ── Currency toggle — the one Ⓩ/$ control every panel that shows a
// currency-ambiguous figure should render, all driven by the SAME lifted
// {currency, onSetCurrency} from DiamondUI's root rather than each surface
// keeping its own local toggle. Multiple copies of this can appear at once
// (header, pulse strip, leaderboard, insights) — that's fine and intentional
// (more places to flip it), since they all read/write the one shared value
// instead of drifting into independent, disagreeing states. ───────────────
export function CurrencyToggle({ currency, onChange, T, size = 10.5, testIdPrefix = "diamond-currency" }) {
  return (
    <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: RADIUS.sm, background: T.surface2 || T.surface, border: `1px solid ${T.border}` }}>
      {[["zbucks", "Ⓩ"], ["usd", "$"]].map(([id, label]) => {
        const on = currency === id;
        return (
          <button key={id} type="button" data-testid={`${testIdPrefix}-${id}`} onClick={() => { hapticTap(); soundTick(); onChange(id); }} style={{
            padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", minWidth: 26,
            background: on ? T.accent : "transparent", color: on ? "#fff" : T.muted,
            fontSize: size, fontWeight: 900, fontFamily: TYPE.mono, transition: "background 160ms ease",
          }}>{label}</button>
        );
      })}
    </div>
  );
}

// ── Ring — win-rate / progress gauge. ────────────────────────────────────
export function Ring({ pct, size = 64, stroke = 7, color, T, label, sub, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.surface3} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color || T.accent} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${(p / 100) * c} ${c}`} style={{ transition: `stroke-dasharray 800ms ${EASE}`, filter: `drop-shadow(0 0 4px ${color || T.accent}88)` }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        {children || (
          <>
            <span style={{ fontSize: size * 0.22, fontWeight: 900, color: T.text, fontFamily: TYPE.mono, lineHeight: 1 }}>{label}</span>
            {sub && <span style={{ fontSize: size * 0.11, fontWeight: 800, color: T.muted, letterSpacing: 0.4, marginTop: 2 }}>{sub}</span>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Sparkline — gradient area + optional pointer scrub. `points` is an
// array of numbers (or {v, label}). onScrub(index|null) reports the hovered
// point so a parent can read the value into its header (Market-Snapshot
// pattern). ───────────────────────────────────────────────────────────────
// `width` also doubles as the internal coordinate space (every point's x is
// computed against it) — a caller that wants a "fill the card" chart passes
// its intended design width AND `responsive`, which stretches the rendered
// SVG to 100% of its actual container via CSS instead of the HTML width
// attribute's fixed pixel size. Without this, a hardcoded width wider than
// the phone's screen (this is how the floor-wide 30-day chart and
// AgentSheet's Hero curve are both built, at width=520) overflows its
// glass() card — which only sets overflowY:auto, not overflowX — and per
// the CSS overflow spec, "visible" on one axis next to non-visible on the
// other computes as auto too, so the whole panel silently became
// horizontally scrollable/draggable instead of the chart just fitting.
// preserveAspectRatio="none" lets width stretch independently of height
// (a sparkline's vertical scale isn't precision-critical) rather than
// letterboxing to preserve the original aspect ratio. getBoundingClientRect()
// in handleMove already reads the real rendered width regardless, so scrub
// accuracy is unaffected either way.
export function Sparkline({ points = [], width = 120, height = 34, color, T, fill = true, strokeWidth = 2, baseline = true, onScrub, scrubIndex, id, live = false, responsive = false }) {
  const vals = points.map((p) => (typeof p === "number" ? p : Number(p?.v || 0)));
  const gid = useMemo(() => id || `spk-${Math.random().toString(36).slice(2, 8)}`, [id]);
  const ref = useRef(null);
  if (vals.length < 2) return <div style={responsive ? { width: "100%", maxWidth: "100%", height } : { width, height }} />;
  const min = Math.min(...vals, baseline ? 0 : Infinity), max = Math.max(...vals, baseline ? 0 : -Infinity);
  const range = max - min || 1;
  // A flat series (every point equal — the Floor Pulse on a quiet tape) sits
  // mid-strip rather than on the bottom edge, where it read as "no line".
  const norm = max === min ? () => 0.5 : (v) => (v - min) / range;
  const pad = 2;
  const pts = vals.map((v, i) => ({ x: pad + (i / (vals.length - 1)) * (width - pad * 2), y: pad + (1 - norm(v)) * (height - pad * 2) }));
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const last = vals[vals.length - 1];
  const c = color || (last >= 0 ? T.green : T.red);
  const zeroY = baseline ? pad + (1 - norm(0)) * (height - pad * 2) : null;
  const handleMove = (e) => {
    if (!onScrub || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const idx = Math.round(((cx - pad) / Math.max(1, rect.width - pad * 2)) * (vals.length - 1));
    onScrub(Math.max(0, Math.min(vals.length - 1, idx)));
  };
  const sp = scrubIndex != null ? pts[scrubIndex] : null;
  return (
    <svg ref={ref} width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio={responsive ? "none" : undefined}
      style={{ display: "block", overflow: "visible", touchAction: onScrub ? "none" : undefined, ...(responsive ? { width: "100%", maxWidth: "100%", height } : {}) }}
      onMouseMove={handleMove} onMouseLeave={() => onScrub?.(null)} onTouchStart={handleMove} onTouchMove={handleMove} onTouchEnd={() => onScrub?.(null)}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity={0.35} />
          <stop offset="100%" stopColor={c} stopOpacity={0} />
        </linearGradient>
      </defs>
      {fill && <path d={`${path} L${pts[pts.length - 1].x.toFixed(1)},${height} L${pts[0].x.toFixed(1)},${height} Z`} fill={`url(#${gid})`} />}
      {zeroY != null && zeroY > pad && zeroY < height - pad && <line x1={pad} x2={width - pad} y1={zeroY} y2={zeroY} stroke={T.border} strokeDasharray="2 3" />}
      <path d={path} fill="none" stroke={c} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 3px ${c}88)` }} />
      {sp ? (
        <>
          <line x1={sp.x} x2={sp.x} y1={pad} y2={height - pad} stroke={c} strokeOpacity={0.4} />
          <circle cx={sp.x} cy={sp.y} r={4} fill={c} stroke={T.bgBase} strokeWidth={1.5} />
        </>
      ) : (
        <>
          {/* A pinned end-value chart doesn't visibly change between polls
              most of the time (this data is realized-trade history — it
              only moves when a trade actually closes), so a constantly
              re-drawing line would be busywork, not "live." A ripple on the
              current-value dot signals "connected to something live"
              honestly instead — reuses the same liveRipple keyframe
              TradingWorkstation's ticker already pulses with. Opt-in via
              `live` since a big scrubbable historical curve (AgentSheet)
              isn't really a "ticking now" context the way a leaderboard row
              is. */}
          {live && (
            <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r={2.6} fill={c}
              style={{ transformBox: "fill-box", transformOrigin: "center", animation: "liveRipple 1.6s ease-out infinite" }} />
          )}
          <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r={2.6} fill={c} />
        </>
      )}
    </svg>
  );
}

// ── Bottom sheet — scrim + spring-in panel, ESC/scrim/handle close. ──────
export function Sheet({ open, onClose, T, children, maxHeight = "92dvh", testId, zIndex = Z.modal, accent }) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (open) { setMounted(true); requestAnimationFrame(() => requestAnimationFrame(() => setShown(true))); }
    else { setShown(false); const t = setTimeout(() => setMounted(false), 260); return () => clearTimeout(t); }
    return undefined;
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const startY = useRef(null);
  const onTouchStart = (e) => { startY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    if (startY.current == null) return;
    const dy = e.changedTouches[0].clientY - startY.current;
    startY.current = null;
    if (dy > 70) onClose?.();
  };
  if (!mounted) return null;
  return (
    <div data-testid={testId} onClick={onClose} style={{ position: "fixed", inset: 0, zIndex, background: T.scrim, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "flex-end", justifyContent: "center", opacity: shown ? 1 : 0, transition: "opacity 220ms ease" }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", maxWidth: 560, maxHeight, display: "flex", flexDirection: "column",
        background: T.bgBase, borderTop: `1px solid ${accent ? accent + "66" : T.borderStrong}`, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}`,
        borderRadius: `${RADIUS.lg + 6}px ${RADIUS.lg + 6}px 0 0`, boxShadow: `0 -18px 60px rgba(0,0,0,0.5)${accent ? `, 0 0 40px ${accent}22` : ""}`,
        transform: shown ? "translateY(0)" : "translateY(40px)", transition: `transform 300ms ${EASE}`,
      }}>
        <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={{ flexShrink: 0, padding: "10px 0 2px", display: "flex", justifyContent: "center", cursor: "grab" }}>
          <span style={{ width: 42, height: 4, borderRadius: RADIUS.pill, background: T.borderStrong }} />
        </div>
        {children}
      </div>
    </div>
  );
}

export function SheetHeader({ title, sub, onClose, T, left, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "6px 16px 10px", flexShrink: 0 }}>
      {left}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: TYPE.title, fontWeight: 900, color: T.text, letterSpacing: -0.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: T.muted, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
      </div>
      {right}
      <button type="button" onClick={onClose} aria-label="Close" style={{
        width: 32, height: 32, borderRadius: RADIUS.pill, border: `1px solid ${T.border}`, background: T.surface2,
        color: T.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
      }}><X size={15} /></button>
    </div>
  );
}

// ── Cumulative P&L series from a closed-trade list (oldest → newest). ────
export function cumulativeSeries(trades = [], key = "pnl", at = "closed_at") {
  const closed = trades.filter((t) => t && t[key] != null && (t.status ? t.status !== "open" : true));
  closed.sort((a, b) => String(a[at] || "").localeCompare(String(b[at] || "")));
  let run = 0;
  return closed.map((t) => { run += Number(t[key] || 0); return { v: Math.round(run * 100) / 100, t: t[at], ticker: t.ticker, pnl: Number(t[key] || 0) }; });
}

// ── Collapsible — accordion row used for explainers. ─────────────────────
export function Collapsible({ title, T, children, defaultOpen = false, icon, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    // flexShrink:0 — see the comment on StatTile above; same trap applies to
    // any overflow:hidden box that can land as a direct child of a bounded
    // flex column (e.g. inside a Sheet).
    <div data-testid={testId} style={{ ...glass(T, { borderRadius: RADIUS.md }), overflow: "hidden", flexShrink: 0 }}>
      <button type="button" onClick={() => { hapticTap(); setOpen((v) => !v); }} style={{
        width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "11px 12px", border: "none", background: "transparent", color: T.text, cursor: "pointer", textAlign: "left",
      }}>
        {icon}
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 800 }}>{title}</span>
        <span style={{ fontSize: 14, color: T.muted, transform: open ? "rotate(180deg)" : "none", transition: `transform 220ms ${EASE}` }}>⌄</span>
      </button>
      <div style={{ maxHeight: open ? 900 : 0, overflow: "hidden", transition: `max-height 320ms ${EASE}` }}>
        <div style={{ padding: "0 12px 12px", fontSize: 12, color: T.sub, lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  );
}

export const useStable = (fn) => {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...a) => ref.current(...a), []);
};
